import type { CustomFieldType, StatusType, ViewType } from './constants';

// ---------- DTOs ----------

export interface StatusDTO {
  id: string;
  statusSetId: string;
  name: string;
  color: string;
  type: StatusType;
  position: string;
}

export interface StatusSetDTO {
  id: string;
  scopeType: 'space' | 'folder' | 'list';
  scopeId: string;
  statuses: StatusDTO[];
}

export interface TagDTO {
  id: string;
  spaceId: string;
  name: string;
  color: string;
}

export interface FieldOption {
  id: string;
  label: string;
  color: string;
}

export interface FieldConfig {
  /** dropdown and labels */
  options?: FieldOption[];
  /** currency: ISO code, e.g. USD */
  currency?: string;
  /** number, currency: decimal places */
  precision?: number;
  /** rating: number of stars (default 5) */
  max?: number;
  /** formula (Phase 9) */
  formula?: string;
}

export type FieldScopeType = 'workspace' | 'space' | 'folder' | 'list';

export interface CustomFieldDTO {
  id: string;
  scopeType: FieldScopeType;
  scopeId: string;
  name: string;
  type: CustomFieldType;
  config: FieldConfig;
  position: string;
}

/** Compact task shape sent in bulk to views. Heavy fields (description) are only in the detail. */
export interface TaskDTO {
  id: string;
  /** Home list. */
  listId: string;
  /** Extra lists the task also appears in. */
  extraListIds: string[];
  parentId: string | null;
  name: string;
  statusId: string;
  priority: number;
  startDate: number | null;
  startHasTime: boolean;
  dueDate: number | null;
  dueHasTime: boolean;
  timeEstimateMs: number | null;
  points: number | null;
  position: string;
  customId: string | null;
  assigneeIds: string[];
  /** Teams the task is assigned to. */
  teamIds: string[];
  tagIds: string[];
  /** Time logged on this task itself (finished entries only). Subtasks are rolled up by the client. */
  timeTrackedMs: number;
  /** Ids of the tasks this one waits on (finished or not), for dependency arrows. */
  waitingOnIds: string[];
  /** Custom field values by field id. */
  fields: Record<string, unknown>;
  subtaskCount: number;
  subtaskDoneCount: number;
  checklistTotal: number;
  checklistDone: number;
  commentCount: number;
  attachmentCount: number;
  /** Download URL of the newest image attachment, for board card covers. */
  coverUrl: string | null;
  /** Number of unfinished tasks this one is waiting on. */
  blockedByCount: number;
  completedAt: number | null;
  archived: boolean;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
}

export interface TaskRef {
  id: string;
  name: string;
  customId: string | null;
  statusId: string;
  listId: string;
}

export interface ChecklistItemDTO {
  id: string;
  checklistId: string;
  name: string;
  assigneeId: string | null;
  done: boolean;
  position: string;
}

export interface ChecklistDTO {
  id: string;
  name: string;
  position: string;
  items: ChecklistItemDTO[];
}

export interface AttachmentDTO {
  id: string;
  resourceType: string;
  resourceId: string;
  filename: string;
  mime: string;
  size: number;
  uploadedBy: string;
  createdAt: number;
  /** Permission-checked download URL (same origin). */
  url: string;
}

/** A doc the task's description points at, as far as the caller may see it. */
export interface DocRef {
  id: string;
  title: string;
  icon: string | null;
}

export interface TaskDetailDTO extends TaskDTO {
  description: unknown;
  watcherIds: string[];
  checklists: ChecklistDTO[];
  /** Tasks this one waits on (they block it). */
  waitingOn: TaskRef[];
  /** Tasks this one blocks. */
  blocking: TaskRef[];
  links: TaskRef[];
  attachments: AttachmentDTO[];
  /** Docs linked from the description (doc mentions and app links), only those the caller can open. */
  docs: DocRef[];
  /** Ancestors from the top level task down to the direct parent. */
  ancestors: TaskRef[];
}

export interface ReactionSummary {
  emoji: string;
  userIds: string[];
}

export interface CommentDTO {
  id: string;
  resourceType: string;
  resourceId: string;
  parentId: string | null;
  authorId: string;
  body: unknown;
  resolved: boolean;
  assignedTo: string | null;
  meta: Record<string, unknown> | null;
  reactions: ReactionSummary[];
  attachments: AttachmentDTO[];
  createdAt: number;
  editedAt: number | null;
}

/** An assigned comment shown on Home, with enough context to link to it. */
export interface AssignedCommentDTO extends CommentDTO {
  resourceName: string;
}

export interface ActivityDTO {
  id: string;
  resourceType: string;
  resourceId: string;
  actorId: string | null;
  action: string;
  data: Record<string, unknown> | null;
  createdAt: number;
}

/** `me`: a person's own pages (Assigned to me, Today & Overdue); the scope id is `<page>:<userId>`. */
export type ViewScopeType = 'workspace' | 'space' | 'folder' | 'list' | 'everything' | 'me';

export interface SortRule {
  field: string;
  dir: 'asc' | 'desc';
}

export interface ViewConfig {
  filter?: FilterGroup;
  sort?: SortRule[];
  /** Field key to group by, or null for no grouping. */
  groupBy?: string | null;
  /** Visible column field keys, in order (list and table views). */
  columns?: string[];
  columnWidths?: Record<string, number>;
  showClosed?: boolean;
  meMode?: boolean;
  subtasks?: 'nested' | 'flat' | 'hidden';
  collapsedGroups?: string[];
  /** Fields shown on board cards. */
  cardFields?: string[];
  /** Show the newest image attachment on board cards (default on). */
  cardCover?: boolean;
  /** Board column colour overrides by group key; unset columns take a palette colour by position. */
  columnColors?: Record<string, string>;
  [key: string]: unknown;
}

export interface ViewDTO {
  id: string;
  scopeType: ViewScopeType;
  scopeId: string;
  type: ViewType;
  name: string;
  config: ViewConfig;
  isPrivate: boolean;
  ownerId: string;
  position: string;
}

export interface TrashedTaskDTO {
  id: string;
  name: string;
  customId: string | null;
  listId: string;
  deletedAt: number;
}

export interface SearchResultDTO {
  type: 'task' | 'doc' | 'message' | 'list' | 'folder' | 'space' | 'clip';
  id: string;
  title: string;
  /** Short context line: list path, snippet. */
  subtitle: string;
  /** Extra ids needed to build a link (e.g. channel id for a message). */
  parentId?: string;
}

/** Realtime payload for task events. */
export interface TaskEventData {
  task: TaskDTO;
  /** Field level changes, e.g. { status: { from, to } }. */
  changes?: Record<string, { from?: unknown; to?: unknown }>;
}

// ---------- Filters ----------

export const FILTER_OPS = [
  'is',
  'is_not',
  'any_of',
  'none_of',
  'contains',
  'not_contains',
  'is_set',
  'is_not_set',
  'gt',
  'gte',
  'lt',
  'lte',
  'before',
  'after',
] as const;
export type FilterOp = (typeof FILTER_OPS)[number];

export interface FilterRule {
  /** `status`, `statusType`, `priority`, `assignee`, `tag`, `dueDate`, `startDate`, `name`, `createdBy`, `list`, `cf:<fieldId>` ... */
  field: string;
  op: FilterOp;
  value?: unknown;
}

export interface FilterGroup {
  op: 'and' | 'or';
  rules: (FilterRule | FilterGroup)[];
}

export function isFilterGroup(node: FilterRule | FilterGroup): node is FilterGroup {
  return Array.isArray((node as FilterGroup).rules);
}

/** Relative date values usable with date filters. */
export const DATE_PRESETS = [
  'today',
  'yesterday',
  'tomorrow',
  'overdue',
  'this_week',
  'next_week',
  'last_7_days',
  'next_7_days',
  'this_month',
] as const;
export type DatePreset = (typeof DATE_PRESETS)[number];

// ---------- Status inheritance ----------

/**
 * The status set a list uses: its own, else its folder's, else its space's (ClickUp style).
 */
export function resolveStatusSet<T extends { scopeType: string; scopeId: string }>(
  list: { id: string; folderId: string | null; spaceId: string },
  sets: T[],
): T | undefined {
  const find = (type: string, id: string | null) =>
    id ? sets.find((s) => s.scopeType === type && s.scopeId === id) : undefined;
  return find('list', list.id) ?? find('folder', list.folderId) ?? find('space', list.spaceId);
}

/**
 * Picks the status in `target` that best matches `current` when a task moves between status sets:
 * same name first, then same type, then the first open status.
 */
export function mapStatus<S extends { id: string; name: string; type: StatusType }>(
  current: { name: string; type: StatusType } | undefined,
  target: S[],
): S | undefined {
  if (target.length === 0) return undefined;
  if (current) {
    const byName = target.find((s) => s.name.toLowerCase() === current.name.toLowerCase());
    if (byName) return byName;
    const byType = target.find((s) => s.type === current.type);
    if (byType) return byType;
  }
  return target.find((s) => s.type === 'open') ?? target[0];
}

export function isClosedType(type: StatusType | undefined): boolean {
  return type === 'done' || type === 'closed';
}

/** Custom fields that apply to a list: workspace wide, plus those on its space, folder and itself. */
export function fieldsForList<F extends { scopeType: string; scopeId: string; position: string }>(
  list: { id: string; folderId: string | null; spaceId: string },
  fields: F[],
): F[] {
  return fields
    .filter(
      (f) =>
        f.scopeType === 'workspace' ||
        (f.scopeType === 'space' && f.scopeId === list.spaceId) ||
        (f.scopeType === 'folder' && f.scopeId === list.folderId) ||
        (f.scopeType === 'list' && f.scopeId === list.id),
    )
    .sort((a, b) => (a.position < b.position ? -1 : a.position > b.position ? 1 : 0));
}
